import React, { useState } from "react";
import { Modal } from "../UI/Modal/Modal";
import Clock from "../../assets/images/clock.svg";
import { classes } from "@/utils/ui";
import { useBalance } from "@/contexts";
import { formatAmount } from "@/utils";

interface Token {
  id: string;
  name: string;
  iconUrl: string;
  balance: string;
}

interface TokenSelectorProps {
  tokens: Token[];
  selectedToken?: Token;
  onSelect: (token: Token) => void;
  className?: string;
}

export const TokenSelector: React.FC<TokenSelectorProps> = ({
  tokens,
  selectedToken,
  onSelect,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { availableToBridge } = useBalance();

  const handleSelect = (token: Token) => {
    onSelect(token);
    setIsOpen(false);
  };

  // If there's only one token, display it as static (no dropdown)
  const showAsDropdown = tokens.length > 1;

  return (
    <div className={classes(`relative`, className)}>
      {/* Dropdown Button */}
      <button
        onClick={showAsDropdown ? () => setIsOpen(true) : undefined}
        className={`w-full flex items-center justify-between p-4 rounded-none bg-bg-dropdownBg border border-none transition-colors ${
          showAsDropdown ? 'hover:border-accentPurple cursor-pointer' : 'cursor-default'
        }`}
      >
        <div className="flex items-center gap-3">
          {selectedToken ? (
            <>
              <div className="w-12 h-12 rounded-full bg-purpleDiagonal flex items-center justify-center">
                <img src={Clock} alt={selectedToken.name} className="w-5 h-5" />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-white font-medium">
                  {selectedToken.name}
                </span>
                <span className="text-sm text-gray-400">
                  Balance:{" "}
                  <span className="text-white">
                    {formatAmount(availableToBridge) ?? 0}
                  </span>
                </span>
              </div>
            </>
          ) : (
            <span className="text-gray-400">Select Token</span>
          )}
        </div>
      </button>

      {/* Modal - only show if there are multiple tokens */}
      {showAsDropdown && (
        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title="Select Token"
          size="md"
        >
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {tokens.map((token) => (
              <button
                key={token.id}
                onClick={() => handleSelect(token)}
                className={`w-full p-3 flex items-center gap-3 rounded-lg border transition-colors ${
                  selectedToken?.id === token.id
                    ? "bg-accentPurple bg-opacity-20 border-accentPurple"
                    : "border-transparent hover:bg-darkBg hover:border-darkBorder"
                }`}
              >
                <div className="w-9 h-9 rounded-full bg-purpleDiagonal flex items-center justify-center">
                  <img src={Clock} alt={token.name} className="w-5 h-5" />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-white font-medium">
                    {token.name}
                  </span>
                  <span className="text-sm text-gray-400">
                    Balance: {token.balance}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
};
